import React, { useState } from 'react';
import { FinancingSacksHectares } from '@indigo-ag/schema';
import {
  ColumnDefinition,
  CustomActionWithPayload,
  RowDefinition,
  Table,
  useFlashMessage,
  useTableState
} from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { ConfirmationDialog, utils } from 'shared';
import { deleteSacksHectares, getAllFinancingSacksHectares } from 'features/pricing/queries';
import { UpdatePricingModal } from '../UpdatePricingModal/UpdatePricingModal';

export interface PricingListProps {
  sacksHectares: FinancingSacksHectares[];
  editable: boolean;
}

type PricingColumnId = 'stateName' | 'stateCode' | 'pricePerBag' | 'bagsPerHectares' | 'campaignName';
type PricingListActions =
  | CustomActionWithPayload<'DELETE', FinancingSacksHectares>
  | CustomActionWithPayload<'EDIT', FinancingSacksHectares>;

interface PricingDefinitionContext {
  canEdit: boolean;
}

const columnDefinition: ColumnDefinition<FinancingSacksHectares, PricingColumnId, PricingDefinitionContext>[] = [
  {
    id: 'stateName',
    header: utils.text.toTitleCase(i18next.t('stateName')),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.state.name} />
  },
  {
    id: 'stateCode',
    header: utils.text.toTitleCase(i18next.t('stateCode')),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.state.code} />
  },
  {
    id: 'pricePerBag',
    header: utils.text.toTitleCase(i18next.t('bagsPriceBase')),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.pricePerBag?.toString() || '-'} />
  },
  {
    id: 'bagsPerHectares',
    header: i18next.t('bagsBase'),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.bagsPerHectares?.toString() || '-'} />
  },
  {
    id: 'campaignName',
    header: utils.text.toTitleCase(i18next.t('campaignName')),
    cell: ({ row, SubtitleCell }) => <SubtitleCell label={row.campaign.name || '-'} />
  }
];

const rowDefinition: RowDefinition<
  FinancingSacksHectares,
  PricingColumnId,
  PricingDefinitionContext,
  PricingListActions
> = {
  rowId: ({ row }) => row.id,
  actions: ({ row, ActionButton, onTableAction, definitionContext }) => {
    return definitionContext?.canEdit ? (
      <>
        <ActionButton destructive onClick={() => onTableAction({ type: 'DELETE', payload: row })}>
          {utils.text.toTitleCase(i18next.t('delete'))}
        </ActionButton>
        <ActionButton onClick={() => onTableAction({ type: 'EDIT', payload: row })}>
          {utils.text.toTitleCase(i18next.t('edit'))}
        </ActionButton>
      </>
    ) : null;
  },
  actionsLocation: 'inline'
};

export const PricingList: React.FunctionComponent<PricingListProps> = ({ sacksHectares, editable }) => {
  /**
   * State
   */
  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
  const [sacksHectaresToDelete, setSacksHectaresToDelete] = useState<FinancingSacksHectares | null>(null);
  const [sacksHectaresToEdit, setSacksHectaresToEdit] = useState<FinancingSacksHectares | null>(null);
  const [updateSacksHectaresModalOpen, setUpdateSacksHectaresModalOpen] = useState(false);

  /**
   * Mutations
   */
  const [deleteSacksHectaresFn] = useMutation(deleteSacksHectares);

  const { addFlashMessage } = useFlashMessage();

  const deleteSacksHectaresRecord = () => {
    return deleteSacksHectaresFn({
      variables: {
        sacksHectaresId: sacksHectaresToDelete!.id
      },
      refetchQueries: [{ query: getAllFinancingSacksHectares }]
    })
      .then(() => {
        addFlashMessage({
          content: i18next.t('pricingInformationDeleted'),
          title: '',
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      });
  };

  const tableState = useTableState<FinancingSacksHectares, PricingColumnId, PricingListActions>(sacksHectares, {
    onTableAction: action => {
      switch (action.type) {
        case 'DELETE':
          setSacksHectaresToDelete(action.payload);
          setDeleteConfirmationDialogOpen(true);
          return;
        case 'EDIT':
          setSacksHectaresToEdit(action.payload);
          setUpdateSacksHectaresModalOpen(true);
          return;
        default:
          return;
      }
    }
  });

  return (
    <>
      <ConfirmationDialog
        open={deleteConfirmationDialogOpen}
        closeDialog={() => setDeleteConfirmationDialogOpen(false)}
        onConfirmPromise={deleteSacksHectaresRecord}
        confirmationText={utils.text.firstLetterUppercase(i18next.t('deletePricingInformationConfirmationMessage'))}
      />
      {sacksHectaresToEdit && (
        <UpdatePricingModal
          open={updateSacksHectaresModalOpen}
          closeModal={() => setUpdateSacksHectaresModalOpen(false)}
          sacksHectares={sacksHectaresToEdit}
        />
      )}
      <Table
        label="Pricing"
        {...tableState}
        columnDefinition={columnDefinition}
        rowDefinition={rowDefinition}
        definitionContext={{ canEdit: editable }}
      />
    </>
  );
};
